import React, { useRef, useEffect, useState, useCallback } from 'react';
import cx from 'classnames';

interface LabelsCarouselProps {
  items: Array<{
    fields?: {
      label: string;
    };
  }>;
  selectedIndex: number;
  onItemClick: (index: number) => void;
  themeClasses: {
    background: string;
    header: string;
    text: string;
    pagination: string;
    pictogramType: string;
  };
  colorTheme: 'light' | 'dark' | string;
  invertColors: boolean;
}

export function LabelsCarousel({
  items,
  selectedIndex,
  onItemClick,
  themeClasses,
  colorTheme
}: LabelsCarouselProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const labelButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [itemWidths, setItemWidths] = useState<number[]>([]);

  // Update dimensions and apply transform (only on mobile/tablet)
  const updateDimensionsAndAlign = useCallback(() => {
    if (!wrapperRef.current) return;

    // Check if we're on desktop (no sliding needed)
    const isDesktop = typeof window !== 'undefined' && window.innerWidth > 1080;

    // Reset transform temporarily for accurate measurements
    wrapperRef.current.style.transform = 'translateX(0px)';

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      if (!wrapperRef.current) return;

      const labelItems = wrapperRef.current.querySelectorAll('.label-item');
      const widths: number[] = [];
      labelItems.forEach((item) =>
        widths.push((item as HTMLElement).offsetWidth)
      );

      setItemWidths(widths);

      // Apply transform after state update (only on mobile/tablet)
      if (!isDesktop) {
        requestAnimationFrame(() => {
          if (!wrapperRef.current) return;

          // Calculate transform directly here to avoid dependency loop
          let cumulativeWidth = 0;
          for (let i = 0; i < selectedIndex; i++) {
            cumulativeWidth += widths[i] + 16; // Add gap between items
          }
          const transformX = -cumulativeWidth;
          wrapperRef.current.style.transform = `translateX(${transformX}px)`;
        });
      }
    });
  }, [selectedIndex]);

  // Calculate item widths and handle resize (trailing debounce + rAF to avoid layout thrashing)
  useEffect(() => {
    updateDimensionsAndAlign();

    let resizeTimer: ReturnType<typeof setTimeout> | undefined;
    let rafId: number | undefined;
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        rafId = window.requestAnimationFrame(() => {
          updateDimensionsAndAlign();
        });
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) clearTimeout(resizeTimer);
      if (rafId !== undefined) window.cancelAnimationFrame(rafId);
    };
  }, [items, updateDimensionsAndAlign]);

  // Apply transform when selectedIndex or itemWidths change (only on mobile/tablet)
  useEffect(() => {
    if (!wrapperRef.current || itemWidths.length === 0) return;

    // Check if we're on desktop (no sliding needed)
    const isDesktop = typeof window !== 'undefined' && window.innerWidth > 1080;

    if (isDesktop) {
      // On desktop, ensure no transform is applied
      wrapperRef.current.style.transform = 'translateX(0px)';
      return;
    }

    // Calculate transform directly to avoid dependency loop
    let cumulativeWidth = 0;
    for (let i = 0; i < selectedIndex; i++) {
      cumulativeWidth += itemWidths[i] + 16; // Add gap between items
    }
    const transformX = -cumulativeWidth;
    wrapperRef.current.style.transform = `translateX(${transformX}px)`;
  }, [selectedIndex, itemWidths]);

  return (
    <div
      ref={containerRef}
      data-testid="app-feature-animation-labels-items"
      className="labels-carousel flex w-full items-start overflow-hidden desktop:overflow-visible"
      role="tablist"
      aria-label="Feature navigation tabs"
    >
      <div
        ref={wrapperRef}
        className="flex min-w-max gap-4 pr-4 transition-transform duration-[700ms] ease-out desktop:flex-col desktop:gap-4 desktop:pr-0"
      >
        {items.map((item, index) => {
          const isSelected = index === selectedIndex;
          const baseClasses = cx(
            'typography-display-2',
            'relative w-fit cursor-pointer',
            themeClasses.header
          );
          const inactiveClasses = cx(
            'opacity-50 hover:opacity-100',
            colorTheme === 'light' && 'hover:text-teal desktop:hover:text-teal',
            colorTheme === 'light' && `!${themeClasses.text}`
          );
          return (
            <div
              key={item.fields?.label || index}
              className="cursor-pointer"
              data-testid="app-feature-animation-label"
            >
              <button
                ref={(el) => (labelButtonRefs.current[index] = el)}
                type="button"
                className={cx(
                  'label-item mb-2 cursor-pointer select-none pt-2 outline-none',
                  baseClasses,
                  !isSelected && inactiveClasses
                )}
                tabIndex={0}
                onFocus={() => onItemClick(index)}
                onClick={() => onItemClick(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onItemClick(index);
                }}
                aria-selected={isSelected}
                aria-controls={`app-feature-animation-medias`}
                role="tab"
              >
                {item.fields?.label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
